import * as React from 'react';

import auth0 from 'auth0-js';
import { AxiosError } from 'axios';
import jwtDecode from 'jwt-decode';

import { useMoon } from '@pricemoov-oss/moon';

import useLogin, { UseLoginResponse } from 'redesign/apiREST/mutations/useLogin';
import useRefreshLogin from 'redesign/apiREST/mutations/useRefreshLogin';
import {
    AUTH0_CLIENT_CONFIG,
    AuthData,
    clearAuthCache,
    clearLastVisitedURL,
    initializeApp,
    normalizeAuthData,
    persistAuthData,
    persistAuthUser,
    restoreAuthData,
    restoreLastVisitedURL,
} from 'redesign/helpers/auth';
import { noop } from 'redesign/helpers/common';
import useGoToURL from 'redesign/hooks/useGoToURL';
import logger from 'services/logger/logger';

export enum UserRole {
    Admin = 'ADMIN',
    NegotiateAdmin = 'NEGOTIATE_ADMIN',
    NegotiateBasic = 'NEGOTIATE_BASIC',
}

export interface IAuthManagerContext {
    /** App login with email and passwoed */
    login: (email: string, password: string) => void;
    /** SSO Login. The connection is the app id associated to the user */
    loginSSO: (connection: string) => void;
    /** Refresh the connection */
    refresh: (token?: string) => void;
    /** Logout from the app */
    logout: () => void;
    /** Merge updated fields of the current user into the persisted auth data */
    updateAuthUser: (user: Partial<AuthData['user']>) => void;
    /** Check if the token is expired */
    isExpired: () => boolean;
    /** Check if the user is authenticated */
    isAuthenticated: () => boolean;
    /** the auth data: token, user data... */
    authData?: AuthData;
    /** True if login is in progress */
    inProgress: boolean;
    /** The auth Error */
    error?: AxiosError;
    /** True if Login error */
    hasError?: boolean;
    /** True if credentials Error (bad email and/or password) */
    hasCredentialsError?: boolean;
}

export const AuthManagerContext = React.createContext<IAuthManagerContext>({
    login: noop,
    loginSSO: noop,
    refresh: noop,
    logout: noop,
    updateAuthUser: noop,
    isExpired: () => true,
    isAuthenticated: () => true,
    inProgress: false,
});

function AuthManager({ children }) {
    const { store } = useMoon();
    const authClient = React.useMemo(() => new auth0.WebAuth(AUTH0_CLIENT_CONFIG), []);
    const [authData, setAuthData] = React.useState<AuthData>(restoreAuthData());
    const goTo = useGoToURL();

    const onSuccess = async (response: UseLoginResponse) => {
        const newAuthData: AuthData = normalizeAuthData({
            ...response.data,
            updated_at: Date.now() / 1000,
        });
        logger.debug(`User "${newAuthData?.user?.email}" has been successfully authenticated!`);
        // persist the auth data in the local storage for the next page refresh
        persistAuthData(newAuthData);
        setAuthData(newAuthData);
        // initialize the app
        await initializeApp(newAuthData);
        // redirect to the last visited URL
        const lastVisitedURL = restoreLastVisitedURL() || '/';
        // Clear the obsolete last visited URL. See WithAuthentication for more information on last visited URL persistence.
        clearLastVisitedURL();
        goTo(lastVisitedURL);
    };

    const [{ isLoading, error }, { dynamicMutate }] = useLogin({
        mutationConfig: {
            onSuccess,
        },
    });

    const [
        { isLoading: isRefreshLoading, error: refreshError },
        { dynamicMutate: dynamicRefresh },
    ] = useRefreshLogin({
        mutationConfig: {
            onSuccess,
        },
    });

    const login = (email: string, password: string) => {
        /**
         * Reset the queries to avoid caching the old data
         * This is a workaround to avoid issues with queries cache data when csm or users are switching between different tenants
         */
        store.resetQueries();
        dynamicMutate({ email, password });
    };

    const loginSSO = (connection: string) => {
        /**
         * Reset the queries to avoid caching the old data
         * This is a workaround to avoid issues with queries cache data when csm or users are switching between different tenants
         */
        store.resetQueries();
        authClient.authorize({ connection });
    };

    const refresh = (token: string = authData?.token) => {
        dynamicRefresh({ token });
    };

    const logout = () => {
        logger.debug('Logging out...');
        // Clear the auth data
        clearAuthCache();
        // Redirect to the login page
        goTo('/login');
        setAuthData(null);
    };

    const updateAuthUser = (user: Partial<AuthData['user']>) => {
        if (!authData?.user) {
            return;
        }
        const newAuthData: AuthData = { ...authData, user: { ...authData.user, ...user } };
        persistAuthUser(newAuthData);
        setAuthData(newAuthData);
    };

    const isExpired = () => {
        // Should refresh if token has passed half life since last refresh
        const decoded: { exp: number } = jwtDecode(authData.token);
        const currentTime = Date.now() / 1000;
        return decoded.exp - currentTime < currentTime - authData.updated_at;
    };

    const isAuthenticated = () => {
        return Boolean(authData?.token) && !isExpired();
    };

    const hasCredentialsError =
        error?.response?.status === 401 || refreshError?.response?.status === 401;

    React.useEffect(() => {
        // Logout if token is expired
        if (authData?.token && isExpired()) {
            logout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authData?.token]);

    return (
        <AuthManagerContext.Provider
            value={{
                login,
                loginSSO,
                logout,
                updateAuthUser,
                isExpired,
                isAuthenticated,
                refresh,
                authData,
                inProgress: isLoading || isRefreshLoading,
                error: error || refreshError,
                hasError: !!(error || refreshError),
                hasCredentialsError,
            }}
        >
            {children}
        </AuthManagerContext.Provider>
    );
}

export function useAuthManager(): IAuthManagerContext {
    return React.useContext(AuthManagerContext);
}

export default AuthManager;
